import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Refresh as RotateIcon,
} from "@mui/icons-material";
import type { Portal } from "@/types";
import apiClient from "@/services/api/client";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import { useExternalApi } from "../hooks/useExternalApi";

interface ExternalApiTabProps {
  portal: Portal;
}

const SCOPE_DESCRIPTIONS: Record<string, string> = {
  "chat:stream": "Chat with agents (SSE streaming)",
  "chat:history": "Read chat threads & messages",
  "agents:read": "List and read agents",
  "users:deprovision": "Deprovision (deactivate) chat users — IdP integration",
};

const ExternalApiTab: React.FC<ExternalApiTabProps> = ({ portal }) => {
  const {
    config,
    isLoading,
    enableApi,
    isEnabling,
    disableApi,
    isDisabling,
    rotateSecret,
    isRotating,
    scopes,
    updateScopes,
    isUpdatingScopes,
  } = useExternalApi(portal.id);

  // Local, editable copy of granted scopes; synced when the server value loads.
  const [selectedScopes, setSelectedScopes] = useState<string[] | null>(null);
  const grantedScopes = scopes?.granted ?? [];
  const effectiveScopes = selectedScopes ?? grantedScopes;
  const scopesDirty =
    selectedScopes !== null &&
    (selectedScopes.length !== grantedScopes.length ||
      [...selectedScopes].sort().join() !== [...grantedScopes].sort().join());

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) => {
      const base = prev ?? grantedScopes;
      return base.includes(scope)
        ? base.filter((s) => s !== scope)
        : [...base, scope];
    });
  };

  const handleSaveScopes = async () => {
    if (!selectedScopes) return;
    setError(null);
    try {
      await updateScopes(selectedScopes);
      setSelectedScopes(null);
    } catch {
      setError("Failed to update scopes");
    }
  };

  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadDocs = async () => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.SYSTEM.EXTERNAL_API.DOCS(portal.id),
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${portal.slug || "portal"}-external-api-docs.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Failed to download documentation";
      setError(msg);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleEnable = async () => {
    setError(null);
    try {
      const result = await enableApi();
      setNewSecret(result.client_secret);
      setShowSecret(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Failed to enable external API";
      setError(msg);
    }
  };

  const handleDisable = () => {
    setError(null);
    disableApi(undefined, {
      onSuccess: () => {
        setConfirmDisable(false);
        setNewSecret(null);
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message || "Failed to disable external API";
        setError(msg);
        setConfirmDisable(false);
      },
    });
  };

  const handleRotate = async () => {
    setError(null);
    setConfirmRotate(false);
    try {
      const result = await rotateSecret();
      setNewSecret(result.client_secret);
      setShowSecret(true);
    } catch (err) {
      const msg = (err as { message?: string })?.message || "Failed to rotate secret";
      setError(msg);
    }
  };

  if (isLoading) {
    return (
      <Stack direction="row" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const isEnabled = config?.enabled ?? false;

  // Build curl snippet for documentation
  const tokenEndpoint = config?.token_endpoint || "https://<cognito-domain>.auth.<region>.amazoncognito.com/oauth2/token";
  const apiBaseUrl = config?.api_base_url || "https://<api-gateway-url>/external/v1";
  const clientId = config?.client_id || "<client_id>";

  const curlCommands = [
    {
      label: "Get access token",
      command: `curl -X POST ${tokenEndpoint} \\\n  -H "Content-Type: application/x-www-form-urlencoded" \\\n  -d "grant_type=client_credentials&client_id=${clientId}&client_secret=<client_secret>&scope=verve-vero-api/chat:stream verve-vero-api/chat:history verve-vero-api/agents:read"`,
    },
    {
      label: "List available agents",
      command: `curl ${apiBaseUrl}/agents \\\n  -H "Authorization: Bearer <access_token>"`,
    },
    {
      label: "Chat with an agent (streaming)",
      command: `curl -N ${apiBaseUrl}/chat/stream \\\n  -H "Authorization: Bearer <access_token>" \\\n  -H "Content-Type: application/json" \\\n  -d '{"agent_id": "<agent_id>", "query": "Hello!"}'`,
    },
    {
      label: "List chat threads",
      command: `curl ${apiBaseUrl}/chat/threads \\\n  -H "Authorization: Bearer <access_token>"`,
    },
  ];

  const curlSnippet = curlCommands
    .map((c, i) => `# ${i + 1}. ${c.label}\n${c.command}`)
    .join("\n\n");

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="body1" fontWeight={600}>
            External API Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Allow external applications to access chat APIs for this portal via OAuth 2.0 client credentials.
          </Typography>
        </Box>
        <Chip
          label={isEnabled ? "Enabled" : "Disabled"}
          color={isEnabled ? "success" : "default"}
          size="small"
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Not enabled — show enable button */}
      {!isEnabled && (
        <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            External API access is not enabled for this portal. Enable it to generate
            OAuth 2.0 credentials that external applications can use to access chat APIs.
          </Typography>
          <Button
            variant="contained"
            onClick={handleEnable}
            disabled={isEnabling}
          >
            {isEnabling ? "Enabling..." : "Enable External API"}
          </Button>
        </Paper>
      )}

      {/* Enabled — show credentials & config */}
      {isEnabled && (
        <Stack spacing={3}>
          {/* One-time secret display */}
          {newSecret && (
            <Alert severity="warning" sx={{ "& .MuiAlert-message": { width: "100%" } }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                Client Secret — copy now, it will not be shown again
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TextField
                  value={showSecret ? newSecret : "••••••••••••••••••••••••••••••••"}
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      readOnly: true,
                      sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                    },
                  }}
                />
                <Tooltip title={showSecret ? "Hide" : "Show"}>
                  <IconButton
                    size="small"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title={copied === "secret" ? "Copied!" : "Copy"}>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(newSecret, "secret")}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Alert>
          )}

          {/* Credentials */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
              Credentials
            </Typography>
            <Stack spacing={2}>
              <CopyableField
                label="Client ID"
                value={config?.client_id ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
              <CopyableField
                label="Token Endpoint"
                value={config?.token_endpoint ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
              <CopyableField
                label="API Base URL"
                value={config?.api_base_url ?? "—"}
                copied={copied}
                onCopy={handleCopy}
              />
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadDocs}
                disabled={isDownloading}
              >
                {isDownloading ? "Downloading..." : "Download API Docs"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RotateIcon />}
                onClick={() => setConfirmRotate(true)}
                disabled={isRotating}
              >
                {isRotating ? "Rotating..." : "Rotate Secret"}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => setConfirmDisable(true)}
                disabled={isDisabling}
              >
                {isDisabling ? "Disabling..." : "Disable External API"}
              </Button>
            </Stack>
          </Paper>

          {/* Scopes (least-privilege per-client grants) */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              API Scopes
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              Grant only the scopes this client needs. Changes apply immediately
              (the secret is unchanged).
            </Typography>
            <FormGroup>
              {(scopes?.available ?? []).map((scope) => (
                <FormControlLabel
                  key={scope}
                  control={
                    <Checkbox
                      size="small"
                      checked={effectiveScopes.includes(scope)}
                      onChange={() => toggleScope(scope)}
                    />
                  }
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {scope}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {SCOPE_DESCRIPTIONS[scope] ?? ""}
                      </Typography>
                    </Stack>
                  }
                />
              ))}
            </FormGroup>
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={!scopesDirty || isUpdatingScopes}
                onClick={handleSaveScopes}
              >
                {isUpdatingScopes ? "Saving..." : "Save Scopes"}
              </Button>
              {scopesDirty && (
                <Button
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => setSelectedScopes(null)}
                  disabled={isUpdatingScopes}
                >
                  Reset
                </Button>
              )}
            </Box>
          </Paper>

          {/* Available Endpoints */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Available Endpoints
            </Typography>
            <Stack spacing={0.75}>
              <EndpointRow method="GET" path="/agents" description="List available agents" />
              <EndpointRow method="GET" path="/agents/{agent_id}" description="Get agent details" />
              <EndpointRow method="POST" path="/chat/stream" description="Chat with an agent (SSE streaming)" />
              <EndpointRow method="GET" path="/chat/threads" description="List chat threads" />
              <EndpointRow method="GET" path="/chat/threads/{id}" description="Get thread with messages" />
              <EndpointRow method="DELETE" path="/chat/threads/{id}" description="Delete a chat thread" />
              <EndpointRow method="POST" path="/users/deprovision" description="Deprovision a chat user (requires users:deprovision scope)" />
              <EndpointRow method="GET" path="/health" description="Health check" />
            </Stack>
          </Paper>

          {/* Code Snippets */}
          <Paper variant="outlined" sx={{ p: 2.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Quick Start
              </Typography>
              <Tooltip title={copied === "snippet-all" ? "Copied!" : "Copy All"}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(curlSnippet, "snippet-all")}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack spacing={1.5}>
              {curlCommands.map((cmd, i) => (
                <Box key={i}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontWeight: 600 }}
                    >
                      {i + 1}. {cmd.label}
                    </Typography>
                    <Tooltip title={copied === `snippet-${i}` ? "Copied!" : "Copy"}>
                      <IconButton
                        size="small"
                        onClick={() => handleCopy(cmd.command, `snippet-${i}`)}
                      >
                        <CopyIcon sx={{ fontSize: "0.875rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "grey.900",
                      borderRadius: 1,
                      overflow: "auto",
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.8rem",
                        color: "grey.100",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        m: 0,
                      }}
                    >
                      {cmd.command}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Stack>
      )}

      {/* Confirm Disable Dialog */}
      <Dialog open={confirmDisable} onClose={() => setConfirmDisable(false)}>
        <DialogTitle>Disable External API</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will immediately revoke all external API access for this portal.
            The Cognito app client and credentials will be permanently deleted.
            External applications using these credentials will stop working immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDisable(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDisable}
            disabled={isDisabling}
          >
            {isDisabling ? "Disabling..." : "Disable"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Rotate Dialog */}
      <Dialog open={confirmRotate} onClose={() => setConfirmRotate(false)}>
        <DialogTitle>Rotate Client Secret</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will generate a new client secret and immediately invalidate the current one.
            Any external applications using the old secret will need to be updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRotate(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRotate}
            disabled={isRotating}
          >
            {isRotating ? "Rotating..." : "Rotate Secret"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** Read-only field with copy button */
const CopyableField: React.FC<{
  label: string;
  value: string;
  copied: string | null;
  onCopy: (value: string, label: string) => void;
}> = ({ label, value, copied, onCopy }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography
        variant="body2"
        sx={{
          fontFamily: "monospace",
          fontSize: "0.85rem",
          wordBreak: "break-all",
          flex: 1,
        }}
      >
        {value}
      </Typography>
      {value !== "—" && (
        <Tooltip title={copied === label ? "Copied!" : "Copy"}>
          <IconButton size="small" onClick={() => onCopy(value, label)}>
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  </Box>
);

/** Endpoint row for the available endpoints list */
const EndpointRow: React.FC<{
  method: string;
  path: string;
  description: string;
}> = ({ method, path, description }) => {
  const methodColors: Record<string, string> = {
    GET: "#4caf50",
    POST: "#2196f3",
    DELETE: "#f44336",
    PUT: "#ff9800",
    PATCH: "#ff9800",
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Chip
        label={method}
        size="small"
        sx={{
          fontFamily: "monospace",
          fontWeight: 700,
          fontSize: "0.7rem",
          minWidth: 60,
          bgcolor: methodColors[method] ?? "grey.500",
          color: "white",
        }}
      />
      <Typography
        variant="body2"
        sx={{ fontFamily: "monospace", fontSize: "0.8rem", minWidth: 200 }}
      >
        {path}
      </Typography>
      <Divider orientation="vertical" flexItem />
      <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
        {description}
      </Typography>
    </Stack>
  );
};

export default ExternalApiTab;
